import * as React from 'react'
import { Carousel } from 'DVWebNodeUI'
import Header from './Header/Header'
import Card from './Card/Card'
import * as style from './FacetedCarousel.module.scss'
import * as csm from '../../../utils/csm'
import InView from 'react-intersection-observer'
import * as cn from 'classnames'
import { CollectionProps } from '../Collection';
import { StorefrontStateItem } from '../../../containers/Storefront';
import { safeBool } from 'DVWebNodeUtils'


interface FacetedCarouselProps extends CollectionProps {
  isIsolatedCarousel?: boolean
}

interface FacetedCarouselState {
  items: StorefrontStateItem[]
  isEditActive: boolean
  isDirty: boolean
  truncateItemList: boolean
}

class FacetedCarousel extends React.PureComponent<FacetedCarouselProps, FacetedCarouselState> {
  state: FacetedCarouselState
  isGettingEnriched: boolean
  isPartiallyEnriched: boolean
  isFullyEnriched: boolean
  containerRef: React.RefObject<HTMLDivElement>
  constructor(props: FacetedCarouselProps) {
    super(props)
    this.state = {
      items: props && props.items,
      isEditActive: false,
      isDirty: false,
      truncateItemList: props.featureSwitches.delayItemsRendering
    }
    this.isGettingEnriched = false
    this.isPartiallyEnriched = false
    this.isFullyEnriched = false

    this.toggleEdit = this.toggleEdit.bind(this)
    this.carouselScrollInteractionHandler = this.carouselScrollInteractionHandler.bind(this)
    this.carouselNavClickInteractionHandler = this.carouselNavClickInteractionHandler.bind(this)
    this.carouselInteractionHandler = this.carouselInteractionHandler.bind(this)
    this.containerRef = React.createRef()
  }


  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::FacetedCarousel')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a FacetedCarousel')
  }

  toggleEdit() {
    this.setState({ isEditActive: !this.state.isEditActive })
  }

  setCollectionDirty() {
    if (!this.state.isDirty) {
      this.setState({ isDirty: true, truncateItemList: false })
    }
  }

  carouselScrollInteractionHandler() {
    csm.count('CarouselScroll')
    this.carouselInteractionHandler()
  }

  carouselNavClickInteractionHandler() {
    csm.count('CarouselPagination')
    this.carouselInteractionHandler()
  }

  componentDidUpdate() {
    // If the carousel contains the focused element, but
    // another has more recently been interacted with, clear
    // the focus, to close the hover (if it's open)
    if (!this.props.collectionHasFocusForHover) {
      const activeElement = document.activeElement as HTMLElement
      const currentContainer = this.containerRef && this.containerRef.current
      if (currentContainer && activeElement && currentContainer.contains(activeElement))
        activeElement.blur()
    }
    if (this.props.filterOn)
      this.setCollectionDirty()
  }

  carouselInteractionHandler() {
    if (!this.props.canLazyLoad || this.isGettingEnriched) {
      this.setCollectionDirty()
    } else if (!this.isFullyEnriched && this.props.enrichCollection) {
      this.isGettingEnriched = true
      this.props.enrichCollection(this.props.collectionKey, true).then(() => {
        this.isGettingEnriched = false
        this.isFullyEnriched = true
        this.setCollectionDirty()
      })
    }
  }

  getCarouselItems(items: StorefrontStateItem[], inView: boolean) {
    const LAZY_THRESHOLD = 8
    const SQUARE_LAZY_THRESHOLD = 14
    const isTwinHeroCarouselType = this.props.collectionType === 'TwinHero'
    const isSquareCarouselType = this.props.collectionType === 'SquareCarousel'
    const isPortraitCarouselType = this.props.collectionType === 'PortraitCarousel'
    // The portrait carousel currently has the same "weight" as the square carousel
    const thisCarouselLazyThreshold = (isSquareCarouselType || isPortraitCarouselType) ? SQUARE_LAZY_THRESHOLD : LAZY_THRESHOLD
    const truncatedItems = this.state.truncateItemList ? items.slice(0, thisCarouselLazyThreshold + 1) : items
    const carouselItems = truncatedItems.filter(item => item).map((item, i) => {

      const isFilteredOut = this.props.filterOn && !safeBool(item.filterEntitled)
      const isAfterThreshold = i > thisCarouselLazyThreshold
      return (<Card
        key={`${item.title} ${i}`}
        {...item}
        collectionType={this.props.collectionType}
        isTwinHero={isTwinHeroCarouselType}
        isSquare={isSquareCarouselType}
        isPortrait={isPortraitCarouselType}
        isEditActive={this.state.isEditActive}
        removeItem={() => this.props.removeItem(i, this.props.collectionKey)}
        triggerCarouselLazyload={this.carouselInteractionHandler}
        isLazy={!this.state.isDirty && isAfterThreshold}
        hoverClass={this.props.hoverClass}
        featureSwitches={this.props.featureSwitches}
        description={item.title}
        isRemoved={isFilteredOut || item.isRemoved}
        pos={i}
        updateChannelItem={this.props.updateChannelItem}
        fetch={this.props.fetch}
        canLivePlayback={this.props.canLivePlayback}
        enrichSingleItem={this.props.enrichSingleItem}
        showProgressBar={Boolean(this.props.edit)}
        isVerticallyVisible={inView}
        isIsolatedCarousel={this.props.isIsolatedCarousel}
        showPlayButton={this.props.showPlayButton}
        isPhablet={this.props.isPhablet}
        showPrerollsInHover={safeBool(this.props.showPrerollsInHover)}
        takeFocusForCarousel={this.props.requestFocusForHover}
        resetFocusForCarousel={this.props.resetFocusForHover}
        isHoverDisabled={this.state.isEditActive}
      />)
    })

    return carouselItems
  }

  render() {
    const props = this.props
    const items = this.props.items

    return (
      <div
        ref={this.containerRef}
        onFocus={() => { this.props.requestFocusForHover && this.props.requestFocusForHover() }}
        className={cn(style.FacetedCarousel, 'tst-faceted-carousel', {
          'tst-editable-carousel': Boolean(this.props.edit),
          ['mobile-collection']: props.isPhablet,
          ['min-width-collection']: props.featureSwitches &&
            props.featureSwitches.requiresMinimumPageWidth,
          [style.isolatedCarousel]: this.props.isIsolatedCarousel,
          [style.overflowVisible]: this.props.featureSwitches.hover2019,
        })}
      >
        <Header
          {...props}
          toggleEdit={this.toggleEdit}
          isEditActive={this.state.isEditActive}
          noMarginLeft={this.props.isIsolatedCarousel}
          seeMoreDescription={this.props.seeMoreDescription}
        />
        <InView triggerOnce={true}>{(inview) => <div
          onScroll={this.carouselScrollInteractionHandler}>
          <Carousel
            unusableVisibleWidth={parseInt(style.unusedCarouselSpace, 10)}
            moveToListener={this.carouselNavClickInteractionHandler}
            wrapChildren={false}
            className={cn({
              [style.focusControl]: !this.state.isEditActive && this.props.collectionHasFocusForHover
            })}
            leftScrollButtonClassName={style.scrollButton}
            rightScrollButtonClassName={style.scrollButton}
          >
            {this.getCarouselItems(items, inview)}
          </Carousel>
        </div>}
        </InView>
      </div>
    )
  }
}

export default FacetedCarousel
